import { useGetAddonsDataQuery } from '@/src/api/rtkQuery/addonsApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import type { PaxPricingInfo, VasAncillary } from '@/src/types/itinerary'

/**
 * Hook for fetching and providing itinerary-related data
 * Centralizes data fetching logic used across pricing calculations
 */
const useItineraryData = () => {
    const itineraryId = useGetItineraryId()

    const {
        faresList,
        fareFamilyEnabledOnReview,
        fareFamilyFares,
        selectedInsurances,
        vasAncillaries,
        paxPricingInfoJson = [],
        passengers = [],
        couponDetails = [],
        giftVoucherData = [],
        meta = {}
    } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => ({
                faresList: data?.faresList || [],
                fareFamilyEnabledOnReview: !!data?.meta?.fareFamilyEnabledOnReview,
                fareFamilyFares: data?.fareFamilyFares || [],
                selectedInsurances: data?.insurance?.selectedInsurances || [],
                vasAncillaries: (data?.itineraryData?.vasAncillaries as VasAncillary[]) || [],
                paxPricingInfoJson:
                    data?.itineraryData && 'paxPricingInfoJson' in data.itineraryData
                        ? (data.itineraryData.paxPricingInfoJson as PaxPricingInfo[]) || []
                        : [],
                passengers: data?.itineraryData?.passengers || [],
                couponDetails: data?.itineraryData?.couponDetails || [],
                giftVoucherData: data?.itineraryData?.giftVoucherData ?? [],
                meta: data?.meta || {}
            })
        }
    )

    const { selectedAncillaries = [] } = useGetAddonsDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data }) => ({
                selectedAncillaries: data?.selectedAncillaries || []
            })
        }
    )

    return {
        faresList,
        fareFamilyEnabledOnReview,
        fareFamilyFares,
        selectedInsurances,
        vasAncillaries,
        paxPricingInfoJson,
        passengers,
        couponDetails,
        giftVoucherData,
        selectedAncillaries,
        meta
    }
}

export default useItineraryData
